/* global __GIT_COMMIT_SHA__ */
import { isReview, isStaging, getCanonicalUrl } from "common/utils/env";
import { datadogRum } from "@datadog/browser-rum";
import { isCrawler } from "common/utils/browser";
import { installHoneybadgerShim } from "common/utils/app-monitoring/honeybadger-shim";
import { shouldIgnoreError } from "common/utils/app-monitoring/should-ignore-error";

const nodeEnv = process.env.NODE_ENV;
const version =
  typeof __GIT_COMMIT_SHA__ !== "undefined" && __GIT_COMMIT_SHA__.length > 0
    ? __GIT_COMMIT_SHA__
    : undefined;
const APP_MONITORING_KEY = "__APP_MONITORING__";
const APP_MONITORING_SHIMS_KEY = "__APP_MONITORING_SHIMS__";
const DATADOG_INITIALIZED_KEY = "__DATADOG_RUM_INITIALIZED__";

// Guard: Prevent double initialization
if (!window[DATADOG_INITIALIZED_KEY] && nodeEnv === "production") {
  const getAppMonitoringFlags = () => {
    const flags = window[APP_MONITORING_KEY]?.flags;
    return {
      datadogEnabled: flags?.datadogEnabled !== false
    };
  };

  if (getAppMonitoringFlags().datadogEnabled) {
    // Mark as initialized BEFORE calling init()
    window[DATADOG_INITIALIZED_KEY] = true;

    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isBotTraffic = isCrawler(userAgent);
    const NOISE_RESOURCE_URL_PATTERN =
      /^(chrome|edge|moz|safari)-extension:|^about:|^data:|^blob:/i;

    let environment;

    if (isStaging()) {
      environment = "staging";
    } else if (isReview()) {
      environment = "review";
    } else {
      environment = "production";
    }

    // Safe switch: allow Datadog-only vs Honeybadger-only rollback.
    // When Datadog is disabled, avoid initializing RUM and shims.
    // Top-level bot traffic check prevents initialization for bots (more efficient)
    if (!isBotTraffic) {
      const applicationId = document.querySelector('meta[name="datadog-application-id"]')?.content;
      const clientToken = document.querySelector('meta[name="datadog-client-token"]')?.content;

      if (applicationId && clientToken) {
        datadogRum.init({
          applicationId,
          clientToken,
          site: "datadoghq.com",
          service: "jiffy-web",
          version,
          env: environment,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 5,
          trackBfcacheViews: true,
          trackResources: true,
          trackLongTasks: true,
          trackUserInteractions: true,
          defaultPrivacyLevel: "mask-user-input",
          trackingConsent: "granted",
          allowedTracingUrls: [
            "https://jiffy.com",
            "https://www.jiffy.com",
            "https://staging.sdtechdev.com",
            /^https:\/\/[^\/]+\.staging\.sdtechdev\.com/ // eslint-disable-line
          ],
          beforeSend: event => {
            // The top level isBotTraffic filter should prevent all bot traffic from being sent to Datadog, but in the case
            // it slips past that for any reason, this is a defensive safeguard to filter out a layer deeper.
            if (isBotTraffic) {
              return false;
            }

            if (event.type === "resource") {
              const resourceUrl = event.resource?.url || "";
              // Drop resource events that originate from browser extensions or inline-only URLs.
              // These do not reflect our site performance and can add noisy data volume.
              if (NOISE_RESOURCE_URL_PATTERN.test(resourceUrl)) {
                return false;
              }
            }

            if (event.type === "error") {
              if (shouldIgnoreError(event)) {
                return false;
              }

              // Sample high-volume errors (keep 10%)
              const message = event.error?.message || "";
              if (message.includes("ChunkLoadError") && Math.random() > 0.1) {
                return false;
              }

              if (
                message.includes("$ is not defined") ||
                message.includes("Can't find variable: $")
              ) {
                // eslint-disable-next-line no-param-reassign
                event.context ??= {};
                // eslint-disable-next-line no-param-reassign
                event.context.broken_asset_urls = window?.brokenAssetURLs;
              }
            }

            return true;
          }
        });

        datadogRum.setTrackingConsent("granted");

        // Setup function that runs after RUM is ready
        // eslint-disable-next-line max-lines-per-function
        const setupDatadog = () => {
          // Start view
          try {
            datadogRum.startView({
              name: window.location.pathname
            });
          } catch (e) {
            // Silently fail
          }

          // Set global context
          try {
            const baseContext = window[APP_MONITORING_KEY]?.context;
            const userContext = window[APP_MONITORING_KEY]?.user;
            let reviewContext = null;

            if (isReview()) {
              try {
                const canonicalUrl = getCanonicalUrl();
                if (canonicalUrl) {
                  const parts = canonicalUrl.split(".");
                  if (parts.length > 0) {
                    const subdomainPart = parts[0];
                    const protocolParts = subdomainPart.split("//");
                    if (protocolParts.length > 1) {
                      reviewContext = { review: protocolParts[1] };
                    }
                  }
                }
              } catch (e) {
                // Silently fail
              }
            }

            const globalContext = {
              ...(baseContext || {}),
              ...(userContext || {}),
              ...(reviewContext || {})
            };

            if (Object.keys(globalContext).length > 0) {
              datadogRum.setGlobalContext(globalContext);
            }
          } catch (e) {
            // Silently fail
          }

          // Install shim
          try {
            window[APP_MONITORING_SHIMS_KEY] = window[APP_MONITORING_SHIMS_KEY] || {};
            window[APP_MONITORING_SHIMS_KEY].installHoneybadgerShim = installHoneybadgerShim;
            installHoneybadgerShim();
          } catch (e) {
            // Silently fail
          }
        };

        // Try onReady first
        let onReadyFired = false;
        const runSetupOnce = () => {
          if (!onReadyFired) {
            onReadyFired = true;
            setupDatadog();
          }
        };

        try {
          datadogRum.onReady(runSetupOnce);
        } catch (e) {
          // Silently fail
        }

        // Fallback: If onReady doesn't fire within 2 seconds, run setup anyway
        setTimeout(runSetupOnce, 2000);
      }
    }
  }
}
